<template>
  <vs-popup :title="$t('video-reuse.title')"
            :active.sync="isActive"
            class="borrow-video-dialog">

    <borrow-video-filter-bar :key="filterKey"
                             :topics="topics"
                             :loading="searching"
                             @course-outline-change="onCourseOutlineChange"
                             @filter-change="onFilterChange"/>

    <!-- Finding 6: getReusableVdoSetTopics no longer commits the shared
         processing state (that flashed the full-screen Loader behind this
         dialog on every search), so this dialog needs its own busy
         affordance instead. -->
    <vs-alert v-if="searching" :active="true" color="primary" icon-pack="feather" icon="icon-loader" class="mb-4">
      {{ $t('video-reuse.searching') }}
    </vs-alert>

    <!-- The preview player replaces the table rather than sitting beside it:
         the dialog is already the full width of the screen and a second video
         element inside a scrolling table fights the sticky header. -->
    <div v-if="previewTopic" class="vx-row mb-4">
      <div class="vx-col w-full">
        <preview-video-card :current-vdo-set-topic="previewTopic"/>
        <vs-button class="mt-2" size="small" type="border" @click.prevent="previewTopic = null">
          {{ $t('general.close') }}
        </vs-button>
      </div>
    </div>

    <div v-if="!previewTopic">
      <vs-alert v-if="!hasCourseOutline" :active="true" color="primary"
                icon-pack="feather" icon="icon-info" class="mb-4">
        {{ $t('video-reuse.select-course-outline-first') }}
      </vs-alert>

      <vs-table-mod v-else
                    :key="tableKey"
                    :data="visibleTopics"
                    :max-items="tableConfig.minItemsPerPage"
                    pagination
                    :no-data-text="$t('video-reuse.no-result')">
        <template slot="thead">
          <vs-th>{{ $t('video-reuse.column-vdo-set') }}</vs-th>
          <vs-th>{{ $t('video-reuse.column-unit') }}</vs-th>
          <vs-th>{{ $t('video-reuse.column-topic') }}</vs-th>
          <vs-th>{{ $t('video-reuse.column-length') }}</vs-th>
          <vs-th>{{ $t('video-reuse.column-size') }}</vs-th>
          <vs-th>{{ $t('video-reuse.column-action') }}</vs-th>
        </template>

        <template slot-scope="{data}">
          <vs-tr v-for="row in data" :key="row.id" :data="row">
            <vs-td>{{ row.vdoSet ? row.vdoSet.code : '' }}</vs-td>
            <vs-td>{{ row.vdoSetUnit ? row.vdoSetUnit.unitSeq : '' }}</vs-td>
            <vs-td>{{ row.no }}. {{ row.nameLocal }}</vs-td>
            <vs-td>{{ formatSeconds(row.vdoLength) }}</vs-td>
            <vs-td>{{ formatSize(row.fileSize) }}</vs-td>
            <vs-td>
              <vs-button size="small" type="border" color="success" class="mr-1"
                         @click.prevent="previewTopic = row">
                {{ $t('video-reuse.preview') }}
              </vs-button>
              <vs-button size="small"
                         @click.prevent="onSelect(row)">
                {{ $t('video-reuse.select') }}
              </vs-button>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table-mod>
    </div>
  </vs-popup>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {confirmGeneral} from '@/utils/dialogs';
import {tableConfig} from '@/utils/table';
import PreviewVideoCard from '@/views/pages/VdoSet/SubComponents/PreviewVideoCard';
import BorrowVideoFilterBar from '@/views/pages/VdoSet/SubComponents/BorrowVideoFilterBar';

const {mapActions: mapVdoSetTopicActions} = createNamespacedHelpers('VdoSetTopic');

export default {
  name: 'BorrowVideoDialog',
  components: {
    PreviewVideoCard,
    BorrowVideoFilterBar,
    vsTableMod: () => import('@/components/vuesax-mod/components/vsTable/vsTable.vue'),
  },
  props: {
    active: {type: Boolean, default: false},
    // The topic being edited. Used to exclude itself from the results and to
    // decide whether picking replaces an existing video.
    topic: {type: Object, default: null},
  },
  data() {
    return {
      // Every borrowable topic in the selected course outline. Fetched once
      // per outline; the filter bar's lower selects narrow it in memory.
      topics: [],
      filter: {vdoSetId: null, vdoSetUnitId: null, vdoSetTopicId: null},
      courseOutlineId: null,
      previewTopic: null,
      // This dialog's own busy flag (Finding 6): getReusableVdoSetTopics does
      // not commit the shared processing state, so nothing outside this
      // component would otherwise show that a fetch is in flight.
      searching: false,
      // Bumped on every open so the filter bar remounts clean, which is
      // cheaper than a reset() method reaching across the component boundary.
      filterKey: 0,
      // Discards a slower fetch that lands after the admin has moved on to a
      // different course outline.
      fetchSeq: 0,
      tableConfig: tableConfig,
    };
  },
  computed: {
    isActive: {
      get() {
        return this.active;
      },
      set(value) {
        this.$emit('update:active', value);
      },
    },
    hasCourseOutline() {
      return !!this.courseOutlineId;
    },
    // Rendered in the order the server returned: getReusableVdoSetTopics sorts
    // by teacher code -> video set -> unit -> topic, and within one course
    // outline the teacher is constant, so this reads set -> unit -> topic.
    visibleTopics() {
      return this.topics.filter((row) => {
        if (this.filter.vdoSetId &&
          (!row.vdoSet || row.vdoSet.id !== this.filter.vdoSetId)) return false;
        if (this.filter.vdoSetUnitId &&
          (!row.vdoSetUnit || row.vdoSetUnit.id !== this.filter.vdoSetUnitId)) return false;
        if (this.filter.vdoSetTopicId && row.id !== this.filter.vdoSetTopicId) return false;
        return true;
      });
    },
    // vs-table-mod keeps its page index in internal state and its data watcher
    // does not reset it, so narrowing the cascade while on page 3 would ask for
    // rows that no longer exist and render "no result" over matching rows.
    // Remounting on any change to what the table shows resets it to page 1.
    tableKey() {
      return [
        this.courseOutlineId,
        this.filter.vdoSetId,
        this.filter.vdoSetUnitId,
        this.filter.vdoSetTopicId,
      ].join('|');
    },
  },
  watch: {
    active(value) {
      if (value) {
        // Reset state left over from the previous time this dialog was open --
        // otherwise reopening it briefly shows the last outline's results.
        this.topics = [];
        this.filter = {vdoSetId: null, vdoSetUnitId: null, vdoSetTopicId: null};
        this.courseOutlineId = null;
        this.previewTopic = null;
        this.filterKey += 1;
        // A fetch left in flight from the last time this was open would
        // otherwise still match fetchSeq and write its rows into the state
        // just cleared above.
        this.fetchSeq += 1;
        this.searching = false;
      }
    },
  },
  methods: {
    ...mapVdoSetTopicActions({
      getReusableVdoSetTopics: 'getReusableVdoSetTopics',
    }),
    async onCourseOutlineChange(courseOutline) {
      this.courseOutlineId = courseOutline ? courseOutline.id : null;
      // A preview cannot outlive the row it came from.
      this.previewTopic = null;

      if (!this.courseOutlineId) {
        // Orphans any fetch in flight. Its finally then skips clearing
        // searching, so clear it here or the busy alert sticks on for good.
        this.fetchSeq += 1;
        this.searching = false;
        this.topics = [];
        return;
      }
      await this.search();
    },
    onFilterChange(filter) {
      this.filter = filter;
      this.previewTopic = null;
    },
    async search() {
      const seq = ++this.fetchSeq;
      this.searching = true;
      try {
        const rows = await this.getReusableVdoSetTopics({
          courseOutlineId: this.courseOutlineId,
          excludeVdoSetTopicId: this.topic ? this.topic.id : null,
          limit: 200,
        });
        // A slower earlier outline must not overwrite a newer one's result.
        if (seq !== this.fetchSeq) return;
        this.topics = rows || [];
      } finally {
        if (seq === this.fetchSeq) this.searching = false;
      }
    },
    formatSeconds(seconds) {
      const total = Number(seconds) || 0;
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
    },
    formatSize(bytes) {
      const n = Number(bytes) || 0;
      if (!n) return '';
      return `${(n / 1024 / 1024).toFixed(1)} MB`;
    },
    onSelect(row) {
      // Replacing an existing video is a different sentence from filling an
      // empty topic, so the confirm text differs. Both go through the shared
      // dialog helper -- no hand-rolled $vs.dialog (CLAUDE.md).
      const hasVideo = !!(this.topic &&
        (this.topic.bunnyVideoGuid || this.topic.vdoStreamPath));
      const message = hasVideo ?
        this.$t('video-reuse.confirm-replace-message') :
        this.$t('video-reuse.confirm-message');

      confirmGeneral(
          this.$vs,
          () => {
            this.$emit('selected', row);
            this.isActive = false;
          },
          null,
          this.$t('video-reuse.confirm-title'),
          message,
      );
    },
  },
};
</script>

<style lang="scss">
// Vuesax caps .vs-popup at 600px, which squeezes the 6-column result table
// into unreadable wrapped cells. Not scoped: the class sits on the popup's
// own root (.con-vs-popup) and the inner .vs-popup is a Vuesax child, so a
// scoped selector would not reach it. The class name is unique to this dialog.
.borrow-video-dialog {
  .vs-popup {
    width: 1000px;
  }

  .vs-popup--content {
    max-height: calc(100vh - 160px);
    // Vuesax gives this box `overflow: auto`, and vue-select renders its menu
    // inline rather than appending it to the body -- so the menu is clipped by
    // whatever height the content happens to have. Before a course outline is
    // picked that is the filter row plus one alert, about 130px, and opening
    // the outline dropdown meant scrolling inside the popup to read the very
    // results you were choosing from. Reserving the height up front fixes all
    // four selects at once: the filter row is always at the top of this box,
    // so every menu opens downward into the space below it.
    //
    // 420px = the filter row (~40px) + vue-select's own 350px menu cap + the
    // 1rem padding this popup gets from _fixesVuesax.scss. Once a table is on
    // screen the content is taller than this anyway and the rule is inert.
    min-height: 420px;
  }
}

// Below this the viewport cannot hold 420px of content plus the popup's own
// chrome. min-height beats max-height in the cascade, so without this the
// popup would grow past the bottom of a short laptop screen instead of
// scrolling -- the scrollbar is the better failure here.
@media (max-height: 600px) {
  .borrow-video-dialog .vs-popup--content {
    min-height: 0;
  }
}
</style>
